import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, Output, EventEmitter, ViewChild, Renderer2, ElementRef, HostListener, TemplateRef } from '@angular/core';
import { NgbDate, NgbCalendar, NgbDateParserFormatter, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { NgbRatingConfig } from '@ng-bootstrap/ng-bootstrap';
// import { Select } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { DeleteModalComponent } from "../modal/delete-modal/delete-modal.component";
import { ConfirmationModalComponent } from "../modal/confirmation-modal/confirmation-modal.component";
import { SharedService } from '@/app/shared/services/shared.service';
import { AuthService } from '@/app/shared/services/auth.service';
import { RowAction } from '@/app/shared/interfaces/table-config.interface';
import { IsAuthurization } from '@/app/shared/interfaces/role.interface';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent {

  @Input() tableConfig: any;
  @Input() cellTemplates: Record<string, TemplateRef<any>> = {};
  @Input() actionIconTemplates: Record<string, TemplateRef<any>> = {};
  @Input() isActionLoading: (action: string, row: any) => boolean = () => false;
  @Input() rowHoverTemplate: TemplateRef<any> | null = null;

  private rowHoverTimer: ReturnType<typeof setTimeout> | undefined;
  private activeRowPopover: NgbPopover | undefined;
  private destroyed = false;

  showRowHover(event: MouseEvent, anchor: HTMLElement, popover: NgbPopover): void {
    this.hideRowHover();
    if (this.destroyed || !this.rowHoverTemplate || popover.disablePopover) return;
    const row = event.currentTarget as HTMLElement;
    this.positionRowHover(event, anchor);
    this.activeRowPopover = popover;
    this.rowHoverTimer = setTimeout(() => {
      this.rowHoverTimer = undefined;
      if (!this.destroyed && row.isConnected && anchor.isConnected && this.activeRowPopover === popover) {
        popover.open();
      }
    }, 500);
  }

  hideRowHover(): void {
    clearTimeout(this.rowHoverTimer);
    this.rowHoverTimer = undefined;
    this.activeRowPopover?.close();
    this.activeRowPopover = undefined;
  }

  positionRowHover(event: MouseEvent | FocusEvent, anchor: HTMLElement): void {
    if (!this.rowHoverTemplate) return;
    const target = event.target as HTMLElement;
    const bounds = target.getBoundingClientRect();
    const x = event instanceof MouseEvent ? event.clientX : bounds.right;
    const y = event instanceof MouseEvent ? event.clientY : bounds.top;
    this.renderer.setStyle(anchor, 'left', `${x + 12}px`);
    this.renderer.setStyle(anchor, 'top', `${y + 12}px`);
  }
  @Input() searchFields: { value: string; label: string; hasArrow?: boolean }[] = [];
  @Input() searchStyle: 'select' | 'radio' | 'select-only' | 'autocomplete' = 'autocomplete';
  @Input() searchMaxLength: number | null = null;
  @Input() hasCheckbox: boolean = false;
  @Input() hasDuplicate: boolean = false;
  @Input() topbar: boolean = true;
  @Input() pagination: boolean = true;
  @Input() loading: boolean = true;
  @Input() dateRange: boolean = false;
  @Input() refreshData: EventEmitter<any>;

  @Output() tableChanged: EventEmitter<any> = new EventEmitter();
  @Output() action = new EventEmitter<any>();
  @Output() rowClicked = new EventEmitter<any>();
  @Output() selectedItems = new EventEmitter<any[]>();
  @Output() searchInput = new EventEmitter<any>();
  @Output() dragDropped = new EventEmitter<any>();
  @Output() countRequested = new EventEmitter<void>();


  @ViewChild("deleteModal") DeleteModal: DeleteModalComponent | undefined;
  @ViewChild("confirmationModal") ConfirmationModal: ConfirmationModalComponent | undefined;

  public tableNames: string[] = [
    'order',
  ]

  public term = new FormControl();
  public searchFieldControl = new FormControl();
  public isSearchOpen: boolean = false;
  public activeSearchTag: { field: string; label: string; value: string } | null = null;
  public rows = [20, 30, 50];
  public tableData: any = {
    'search': '',
    'searchField': '',
    'field': '',
    'sort': '', // current Sorting Order
    'page': 1, // current page number
    'paginate': 20, // Display per page,
  };
  public qPageSize = -1;

  public selected: number[] = [];
  public permissions: number[] = [];

  public hoveredDate: NgbDate | null = null;
  public fromDate: NgbDate | null | undefined;
  public toDate: NgbDate | null | undefined;

  private searchSubject = new Subject<string>();
  private directSearchSubject = new Subject<string>();
  private readonly debounceTimeMs = 500;
  public link: string;

  constructor(@Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2, config: NgbRatingConfig,
    private calendar: NgbCalendar,
    public formatter: NgbDateParserFormatter,
    private sharedService: SharedService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private elementRef: ElementRef
  ) {
    this.router.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event instanceof NavigationStart) this.hideRowHover();
    });
    config.max = 5;     // customize default values of ratings used by this component tree
    config.readonly = true;

    this.searchFieldControl.valueChanges.pipe(
      distinctUntilChanged())
      .subscribe((field: string) => {
        this.tableData['searchField'] = field;
      });

    this.searchSubject.pipe(debounceTime(this.debounceTimeMs)).subscribe((searchValue) => {
      this.searchInput.emit(searchValue);
    });

    this.authService.getPermission().subscribe(res => {
      this.permissions = res;
    });
  }

  ngOnInit() {
    const searchFieldOption = this.tableConfig?.filters?.searchField ||
      (this.searchFields?.length ? this.searchFields[0]?.value : undefined) ||
      (this.tableConfig?.searchFields?.length ? this.tableConfig.searchFields[0]?.value : undefined);

    if (searchFieldOption) {
      this.searchFieldControl.setValue(searchFieldOption, { emitEvent: false });
      this.tableData['searchField'] = searchFieldOption;
    }

    if (this.tableConfig?.filters?.searchTerm) {
      const fieldVal = searchFieldOption || (this.tableConfig?.tableName === 'order' ? 'OrderId' : '');
      const fieldObj = (this.searchFields && this.searchFields.length ? this.searchFields : this.tableConfig?.searchFields)?.find((f: any) => f.value === fieldVal);
      const defaultLabel = this.tableConfig?.tableName === 'order' ? 'Mã đơn' : 'Tìm kiếm';
      const label = fieldObj ? fieldObj.label : defaultLabel;
      this.activeSearchTag = {
        field: fieldVal,
        label: label,
        value: this.tableConfig.filters.searchTerm
      };
      this.term.setValue('', { emitEvent: false });
    }
    this.setDateForDatePicker();

    // Debounce search tự động khi gõ (cho các table không có searchFields autocomplete)
    this.directSearchSubject.pipe(debounceTime(this.debounceTimeMs)).subscribe((val) => {
      if (this.effectiveSearchFields.length === 0) {
        this.onChangeTable(val, 'search');
      }
    });

    this.link = window.location.pathname;
    if (this.tableConfig && this.tableConfig.rowActions) {
      for (let i = 0; i < this.tableConfig.rowActions.length; i++) {
        const action = this.tableConfig.rowActions[i];
        const permission = action.permission;
        if (permission && permission.length) {
          if (permission.some((p: number) => !this.permissions.includes(p))) {
            this.tableConfig.rowActions.splice(i, 1);
            i--;
          }
        }
      }
    }
    if (!this.hasPermission(['delete']) && !this.hasDuplicate) {
      this.hasCheckbox = false;
    }

    this.qPageSize = this.sharedService.getPageSize();
    let page = this.sharedService.getCurrentPage();
    if (this.qPageSize > 0) {
      this.tableData['paginate'] = this.qPageSize;
    }
    if (page > 0) {
      this.tableData['page'] = page;
    }

    if (this.refreshData) {
      this.refreshData.subscribe((data: any) => {
        if (data && data.page) {
          this.tableData['page'] = data.page;
        } else {
          this.tableData['page'] = 1;
        }
        this.tableChanged.emit(this.tableData);
      })
    }
  }

  setDateForDatePicker() {
    if (this.tableConfig.filters?.fromDate) {
      let from = new Date(this.tableConfig.filters.fromDate);
      this.fromDate = NgbDate.from({
        year: from.getFullYear(),
        month: from.getMonth() + 1,
        day: from.getDate()
      });
    }

    if (this.tableConfig.filters?.toDate) {
      let to = new Date(this.tableConfig.filters.toDate);
      this.toDate = NgbDate.from({
        year: to.getFullYear(),
        month: to.getMonth() + 1,
        day: to.getDate()
      });
    }
  }

  drop(event: any) {
    moveItemInArray(this.tableConfig.data, event.previousIndex, event.currentIndex);
    this.dragDropped.emit(event);
  }

  ngOnChanges() {
    if (this.route.snapshot.queryParams['pageSize'] && typeof parseInt(this.route.snapshot.queryParams['pageSize']) === 'number') {
      this.qPageSize = parseInt(this.route.snapshot.queryParams['pageSize']);
    }
    this.selected = [];
  }

  hasPermission(actions?: string[]) {
    let permission = this.tableConfig?.rowActions?.find((action: any) => actions?.includes(action.actionToPerform))?.permission;
    return IsAuthurization(this.permissions, permission);
  }

  isActionVisible(action: any, columnData?: any): boolean {
    if (!action) return false;
    if (action.hidden === true || action.hide === true) return false;
    if (typeof action.isVisible === 'function' && !action.isVisible(columnData)) return false;
    return true;
  }

  isActionEnabled(action: any, columnData: any): boolean {
    if (!action) return false;
    if (!this.hasPermission([action.actionToPerform])) return false;
    if (typeof action.disabled === 'function' && action.disabled(columnData)) return false;
    if (action.disabled === true) return false;
    if (action.hideIfEmpty && !columnData?.[action.actionToPerform + '_show']) return false;
    if (columnData?.[action.actionToPerform + '_disabled']) return false;
    if (columnData?.[action.actionToPerform + '_show'] === false) return false;
    return true;
  }

  onDisabledActionClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  getActionTooltip(action: any, columnData: any): string {
    if (!action) return '';
    if (this.isActionLoading(action.actionToPerform, columnData)) {
      return 'Đang xử lý...';
    }
    if (!this.isActionEnabled(action, columnData)) {
      if (columnData?.[action.actionToPerform + '_disabled_message']) {
        return columnData[action.actionToPerform + '_disabled_message'];
      }
      if (!this.hasPermission([action.actionToPerform])) {
        return 'Bạn không có quyền thực hiện thao tác này';
      }
      return 'Không khả dụng';
    }
    return columnData?.[action.actionToPerform + '_label'] || '';
  }

  onChangeTable(data: any | any, type: string) {
    if (type === 'search' && !this.isSearchLengthValid(data ?? '')) return;
    if (type === 'sort' && data && data.sortable !== false) {
      if (this.tableConfig.lazyCount) this.tableData['page'] = 1;
      switch (data.sort_direction) {
        case 'asc':
          data.sort_direction = 'desc';
          break;
        case 'desc':
          data.sort_direction = 'asc';
          break;
        default:
          data.sort_direction = 'desc';
          break;
      }
      this.tableData['field'] = data.dataField!;
      this.tableData['sort'] = this.tableData['sort'] === 'desc' ? 'asc' : 'desc';
      data.sortCount ??= 0;
      data.sortCount++;
      if (data.sortCount > 2) {
        this.tableData['field'] = '';
        this.tableData['sort'] = '';
        data.sortCount = 0;
        data.sort_direction = '';
      }
    } else if (type === 'paginate') {
      this.tableData['paginate'] = (<HTMLInputElement>data.target)?.value;
      this.router.navigate([], { relativeTo: this.route, queryParams: { pageSize: this.tableData['paginate'] }, queryParamsHandling: 'merge', replaceUrl: true });
      // Get Pager For Pagination
      if (this.tableConfig.lazyCount) {
        this.tableData['page'] = 1;
        this.router.navigate([], { relativeTo: this.route, queryParams: { page: 1 }, queryParamsHandling: 'merge', replaceUrl: true });
      } else {
        const pager = this.sharedService.getPager(this.tableConfig.total, this.tableData["page"], this.tableData['paginate'], this.tableConfig.paginateRange);
        this.tableData["page"] = pager.currentPage;
      }
    } else if (type === 'page') {
      this.tableData['page'] = data;
    } else if (type === 'search') {
      this.tableData["page"] = 1;
      this.tableData['search'] = data;
    } else if (type === 'daterange') {
      if (data) {
        this.tableData['start_date'] = data.start_date;
        this.tableData['end_date'] = data.end_date;
      } else {
        this.tableData['start_date'] = null;
        this.tableData['end_date'] = null;
      }
    }
    this.renderer.addClass(this.document.body, 'loader-none');
    this.tableChanged.emit(this.tableData);
  }

  onSwitchClick(event: any, columnHead: any, columnData: any, index: number) {
    if (columnHead?.titleModal || columnHead?.messageModal) {
      this.ConfirmationModal?.openModal(
        columnHead?.dataField.toString(),
        columnData,
        columnData[columnHead?.dataField.toString()] == '1' ? 0 : 1,
        columnHead?.titleModal ? columnHead?.titleModal : '',
        columnHead?.messageModal ? columnHead?.messageModal : ''
      );
      event.preventDefault();
      event.stopPropagation();
    } else {
      this.onActionClicked(
        columnHead?.dataField.toString(),
        columnData,
         columnData[columnHead?.dataField.toString()] == '1' ? 0 : 1,
        index
      );
    }
  }

  onActionClicked(actionType: string, rowData: any, value?: number, index?: number) {
    if (this.isActionLoading(actionType, rowData)) return;

    const action = this.tableConfig?.rowActions?.find((a: any) => a.actionToPerform === actionType);
    if (action && !this.isActionEnabled(action, rowData)) return;

    this.renderer.addClass(this.document.body, 'loader-none');
    if (this.hasPermission([actionType])) {
      rowData[actionType] = value;
      this.action.emit({ actionToPerform: actionType, data: rowData, index: index });
    } else {
      rowData[actionType] = value;
      this.action.emit({ actionToPerform: actionType, data: rowData, index: index });
    }
  }

  private rowMouseDownPos: { x: number; y: number } | null = null;

  onRowMouseDown(event: MouseEvent): void {
    this.rowMouseDownPos = { x: event.clientX, y: event.clientY };
    this.hideRowHover();
  }

  onRowClicked(rowData: any, event?: MouseEvent): void {
    const selection = typeof window !== 'undefined' ? window.getSelection() : null;
    if (selection && selection.toString().trim().length > 0) {
      return;
    }
    if (this.rowMouseDownPos && event) {
      const dist = Math.hypot(event.clientX - this.rowMouseDownPos.x, event.clientY - this.rowMouseDownPos.y);
      if (dist > 5) {
        return;
      }
    }
    if (this.tableConfig?.rowClickPermission
      ? IsAuthurization(this.permissions, this.tableConfig.rowClickPermission)
      : this.hasPermission(['edit', 'view'])) {
      this.rowClicked.emit(rowData);
    }
  }

  checkUncheckAll(event: Event) {
    this.tableConfig?.data!.forEach((item: any) => {
      // if(item.system_reserve != '1') { 
      item.isChecked = (<HTMLInputElement>event?.target)?.checked;
      this.setSelectedItem((<HTMLInputElement>event?.target)?.checked, item?.id);
      // };
    });
  }

  onItemChecked(event: Event) {
    this.setSelectedItem((<HTMLInputElement>event.target)?.checked, (<HTMLInputElement>event.target)?.value);
  }

  setSelectedItem(checked: Boolean, value: any) {
    const index = this.selected.indexOf(value);
    if (checked) {
      if (index == -1) this.selected.push(value)
    } else {
      this.selected = this.selected.filter(id => id != value);
    }
    this.selectedItems.emit(this.selected);
  }

  get deleteButtonStatus() {
    let status = false;
    this.tableConfig?.data?.filter((data: any) => {
      if (this.selected.includes(data?.id)) {
        // const permission = this.tableConfig?.rowActions?.find((action: any) => action.actionToPerform == 'delete')?.permission as string;
        // if(permission && this.permissions?.includes(permission)){
        //   status = true;
        // }
        status = true;
      }
    });
    return status;
  }

  get duplicateButtonStatus() {
    let status = false;
    this.tableConfig?.data?.filter((data: any) => {
      if (this.selected.includes(data?.id)) {
        const permission = this.tableConfig?.rowActions?.find((action: RowAction) => action.actionToPerform.toLowerCase() === 'edit')?.permission;
        status = IsAuthurization(this.permissions, permission);
      }
    });
    return status;
  }

  // For Date Picker

  onDateSelection(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date && date.after(this.fromDate)) {
      this.toDate = date;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }

    let params = {
      start_date: `${this.fromDate.year}-${this.fromDate.month}-${this.fromDate.day}`,
      end_date: this.toDate ? `${this.toDate?.year}-${this.toDate?.month}-${this.toDate?.day}` : null
    }
    this.onChangeTable(params, 'daterange')
  }

  isHovered(date: NgbDate) {
    return (
      this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
    const parsed = this.formatter.parse(input);
    return parsed && this.calendar.isValid(NgbDate.from(parsed)) ? NgbDate.from(parsed) : currentValue;
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.hideRowHover();
    this.renderer.removeClass(this.document.body, 'loader-none');
  }

  clearDateRange() {
    this.tableData['page'] = 1;
    this.fromDate = null;
    this.toDate = null;
    this.onChangeTable(null, 'daterange');
  }

  get effectiveSearchFields(): { value: string; label: string; hasArrow?: boolean }[] {
    if (this.searchFields && this.searchFields.length) {
      return this.searchFields;
    }
    if (this.tableConfig && this.tableConfig.searchFields && this.tableConfig.searchFields.length) {
      return this.tableConfig.searchFields;
    }
    return [];
  }

  toggleDropdown(event: Event) {
    event.stopPropagation();
    this.isSearchOpen = !this.isSearchOpen;
  }

  selectSearchOption(item: any, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!this.isSearchLengthValid(this.term.value ?? '')) return;
    const val = this.term.value ? this.term.value.trim() : '';
    if (!val) {
      this.isSearchOpen = false;
      return;
    }
    this.searchFieldControl.setValue(item.value);
    this.tableData['searchField'] = item.value;
    this.activeSearchTag = {
      field: item.value,
      label: item.label,
      value: val
    };
    this.term.setValue('', { emitEvent: false });
    this.isSearchOpen = false;
    this.onChangeTable(val, 'search');
  }

  onEnterSearch(event?: KeyboardEvent) {
    if (event?.isComposing) return;
    event?.preventDefault();
    if (!this.isSearchLengthValid(this.term.value ?? '')) return;
    const val = this.term.value ? this.term.value.trim() : '';
    if (val) {
      const fields = this.effectiveSearchFields;
      if (fields.length > 0) {
        // Table có cấu hình searchFields (vd: Order) → dùng autocomplete tag
        const currentField = this.searchFieldControl.value || fields[0]?.value;
        const item = fields.find(f => f.value === currentField) || fields[0];
        if (item) {
          this.selectSearchOption(item);
          return;
        }
      } else {
        // Table không có searchFields (các module khác) → search trực tiếp
        this.isSearchOpen = false;
        this.onChangeTable(val, 'search');
        return;
      }
    }
    this.isSearchOpen = false;
  }

  removeActiveTag(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.activeSearchTag = null;
    this.term.setValue('', { emitEvent: false });
    this.isSearchOpen = false;
    this.onChangeTable('', 'search');
  }

  onFocusInput() {
    if (this.activeSearchTag) {
      this.isSearchOpen = false;
      return;
    }
    this.isSearchOpen = true;
  }

  onSearchKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        this.navigateSearchOptions(event, 1);
        break;
      case 'ArrowUp':
        this.navigateSearchOptions(event, -1);
        break;
      case 'Enter':
        this.onEnterSearch(event);
        break;
      case 'Escape':
        this.isSearchOpen = false;
        break;
    }
  }

  navigateSearchOptions(event: KeyboardEvent, direction: 1 | -1) {
    const fields = this.effectiveSearchFields;
    if (event.isComposing || this.activeSearchTag || !this.term.value?.trim() || !fields.length) return;

    event.preventDefault();
    this.isSearchOpen = true;
    const currentIndex = fields.findIndex(field => field.value === this.searchFieldControl.value);
    const nextIndex = currentIndex < 0
      ? (direction === 1 ? 0 : fields.length - 1)
      : (currentIndex + direction + fields.length) % fields.length;
    this.searchFieldControl.setValue(fields[nextIndex].value);
  }

  private isSearchLengthValid(value: string): boolean {
    return this.searchMaxLength === null || value.length <= this.searchMaxLength;
  }

  onSearchInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let val = input.value;
    if (!this.isSearchLengthValid(val)) {
      val = val.slice(0, this.searchMaxLength!);
      input.value = val;
      this.term.setValue(val, { emitEvent: false });
    }
    if (this.activeSearchTag) {
      this.isSearchOpen = false;
      return;
    }
    if (this.effectiveSearchFields.length === 0) {
      this.directSearchSubject.next(val.trim());
    } else {
      this.isSearchOpen = true;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.isSearchOpen && !this.elementRef.nativeElement.querySelector('.table-search')?.contains(event.target as Node)) {
      this.isSearchOpen = false;
    }
  }

  input(value: string) {
    if (value && value != this.term.value) {
      this.searchSubject.next(value);
    }
    else if (value != undefined) {
      this.searchInput.emit("");
    }
  }

}
